import { useState, useEffect, useRef, useCallback } from 'react';
import { Chart } from 'primereact/chart';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Tooltip } from 'primereact/tooltip';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useAuth0 } from '@auth0/auth0-react';
import { fetchAccessToken, apiCallErrorHandler } from '../utils/common';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Format a Date using local time to avoid UTC offset shifting the day
const toLocalDateStr = (d) => {
    if (!d) return null;
    const y  = d.getFullYear();
    const m  = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
};

function ChartTitle({ title, tooltip, id }) {
    return (
        <h4 className="flex align-items-center gap-2">
            {title}
            <i
                id={id}
                className="pi pi-info-circle text-color-secondary"
                style={{ fontSize: '0.95rem', cursor: 'default' }}
                data-pr-tooltip={tooltip}
                data-pr-position="right"
            />
            <Tooltip target={`#${id}`} />
        </h4>
    );
}

const PALETTE = [
    '#4e79a7', '#f28e2b', '#e15759', '#76b7b2',
    '#59a14f', '#edc948', '#b07aa1', '#ff9da7',
    '#9c755f', '#bab0ac',
];

// Generate a visually distinct soft color for any index using the golden-ratio hue spread
function distinctColor(i) {
    const hue = (i * 137.508) % 360;
    return `hsl(${hue.toFixed(0)}, 45%, 62%)`;
}

const chartOptions = {
    responsive: true,
    plugins: {
        legend: { position: 'top' },
    },
    scales: {
        y: { beginAtZero: true },
    },
};

const completenessOptions = {
    ...chartOptions,
    scales: {
        y: { beginAtZero: true, max: 100, ticks: { callback: (v) => `${v}%` } },
    },
    plugins: {
        ...chartOptions.plugins,
        tooltip: {
            callbacks: {
                label: (ctx) => `${ctx.dataset.label}: ${ctx.raw}%`,
            },
        },
    },
};

export default function DashboardPage() {
    const toast = useRef(null);
    const filterOptionsPopulated = useRef(false);
    const { getAccessTokenSilently } = useAuth0();
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [data, setData] = useState([]);

    useEffect(() => { document.title = 'OptiConfig Dashboard'; }, []);

    const defaultFrom = (() => { const d = new Date(); d.setMonth(d.getMonth() - 1); d.setHours(0,0,0,0); return d; })();
    const defaultTo   = (() => { const d = new Date(); d.setHours(0,0,0,0); return d; })();

    const [filters, setFilters] = useState({
        market: null,
        brand: null,
        source: null,
        startedFrom: defaultFrom,
        startedTo: defaultTo,
    });
    const [appliedFilters, setAppliedFilters] = useState({
        market: null,
        brand: null,
        source: null,
        startedFrom: defaultFrom,
        startedTo: defaultTo,
    });

    const [sourceUtilizationChart, setSourceUtilizationChart] = useState(null);
    const [sourceCompletenessChart, setSourceCompletenessChart] = useState(null);
    const [foundPrcChart, setFoundPrcChart] = useState(null);
    const [metricsRadarChart, setMetricsRadarChart] = useState(null);
    const [metricsAggChart, setMetricsAggChart] = useState(null);
    const [viewStats, setViewStats] = useState(null);
    const [filterOptions, setFilterOptions] = useState({ markets: [], brands: [], sources: [] });

    const fetchData = useCallback(async (activeFilters) => {
        setLoading(true);
        try {
            const token = await fetchAccessToken(getAccessTokenSilently);
            const params = new URLSearchParams();
            if (activeFilters.market) params.append('market', activeFilters.market);
            if (activeFilters.startedFrom)
                params.append('startedFrom', toLocalDateStr(activeFilters.startedFrom));
            if (activeFilters.startedTo)
                params.append('startedTo', toLocalDateStr(activeFilters.startedTo));

            const response = await fetch(
                `${API_BASE_URL}/Dashboard/brand-source-stats?${params}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!response.ok)
                await apiCallErrorHandler(response, 'Failed to fetch dashboard data');
            const json = await response.json();
            setData(json);
            if (!filterOptionsPopulated.current) {
                filterOptionsPopulated.current = true;
                setFilterOptions({
                    markets: [...new Set(json.map((r) => r.market))].sort().map((m) => ({ label: m, value: m })),
                    brands:  [...new Set(json.map((r) => r.brand))].sort().map((b) => ({ label: b, value: b })),
                    sources: [...new Set(json.map((r) => r.source))].sort().map((s) => ({ label: s, value: s })),
                });
            }
        } catch (ex) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: ex.message });
        } finally {
            setLoading(false);
        }
    }, [getAccessTokenSilently]);

    // Load on mount with empty filters
    useEffect(() => {
        fetchData(filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Rebuild charts when data or applied client-side filters change
    useEffect(() => {
        // Charts 1–3 only respect brand filter; source filter only drives charts 4–5
        const brandFilteredData = data
            .filter((r) => !appliedFilters.brand || r.brand === appliedFilters.brand);

        // Charts 4–5 respect both filters
        const chartData = brandFilteredData
            .filter((r) => !appliedFilters.source || r.source === appliedFilters.source);

        if (!brandFilteredData.length) {
            setSourceUtilizationChart(null);
            setSourceCompletenessChart(null);
            setFoundPrcChart(null);
            setMetricsAggChart(null);
            setMetricsRadarChart(null);
            setViewStats(null);
            return;
        }

        const sources = [...new Set(brandFilteredData.map((r) => r.source))]
            .filter((src) => !appliedFilters.source || src === appliedFilters.source)
            .sort();

        // --- View stats ---
        const statData    = appliedFilters.source ? brandFilteredData.filter((r) => r.source === appliedFilters.source) : brandFilteredData;
        const statSessions = new Set(statData.map((r) => r.sessionId)).size;
        const statBrands   = new Set(statData.map((r) => r.brand)).size;
        const statSources  = new Set(statData.map((r) => r.source)).size;
        const maxInSessionBySession = new Map();
        const maxInSessionVinBySession = new Map();
        statData.forEach((r) => {
            const cur = maxInSessionBySession.get(r.sessionId) ?? 0;
            if (r.inSessionCount > cur) maxInSessionBySession.set(r.sessionId, r.inSessionCount);
            const curVin = maxInSessionVinBySession.get(r.sessionId) ?? 0;
            if (r.inSessionVinCount > curVin) maxInSessionVinBySession.set(r.sessionId, r.inSessionVinCount);
        });
        const statVins = [...maxInSessionBySession.values()].reduce((s, v) => s + v, 0);
        const statVinsCount = [...maxInSessionVinBySession.values()].reduce((s, v) => s + v, 0);
        setViewStats({ sessions: statSessions, brands: statBrands, sources: statSources, vins: statVins, vinsCount: statVinsCount });

        // Stable color map: assign colors by global source order so the same
        // source always gets the same color across all charts.
        const allSources = [...new Set(data.map((r) => r.source))].sort();
        const sourceColorMap = Object.fromEntries(
            allSources.map((src, i) => [src, distinctColor(i)])
        );

        // --- Chart 1: Source Utilization % ---
        // When brand filter is active, scope sessions to those containing that brand.
        // Source filter only controls charts 4–5; percentages here are relative to the brand-scoped session total.
        const sessionPool = appliedFilters.brand ? brandFilteredData : data;
        const vizSources = [...new Set(data.map((r) => r.source))]
            .filter((src) => !appliedFilters.source || src === appliedFilters.source)
            .sort();
        const totalSessions = new Set(sessionPool.map((r) => r.sessionId)).size;
        const utilizationData = vizSources.map((src) => {
            const sessionsWithSource = new Set(
                sessionPool.filter((r) => r.source === src).map((r) => r.sessionId)
            ).size;
            return parseFloat(((sessionsWithSource / totalSessions) * 100).toFixed(1));
        });
        setSourceUtilizationChart({
            labels: vizSources,
            datasets: [
                {
                    label: 'Sessions Using Source (%)',
                    data: utilizationData,
                    backgroundColor: vizSources.map((src) => sourceColorMap[src]),
                },
            ],
        });

        // --- Chart 2: Avg Data Completeness (%) by Brand per Source — top 10 brands by total foundCount, rest → "Others" ---
        const TOP_N = 10;
        const chartBrands = [...new Set(chartData.map((r) => r.brand))];
        const brandTotals = chartBrands.map((b) => ({
            brand: b,
            total: chartData.filter((r) => r.brand === b).reduce((s, r) => s + r.foundCount, 0),
        }));
        brandTotals.sort((a, b) => b.total - a.total);
        const top10Brands = brandTotals.slice(0, TOP_N).map((b) => b.brand);
        const otherBrands = brandTotals.slice(TOP_N).map((b) => b.brand);
        const completenessLabels = otherBrands.length ? [...top10Brands, 'Others'] : top10Brands;

        setSourceCompletenessChart({
            labels: completenessLabels,
            datasets: sources.map((src) => ({
                label: src,
                data: completenessLabels.map((label) => {
                    const brandSet = label === 'Others' ? otherBrands : [label];
                    const rows = chartData.filter((r) => r.source === src && brandSet.includes(r.brand));
                    if (!rows.length) return null;
                    const avg = rows.reduce((s, r) => s + r.nonEmptyFieldsPct, 0) / rows.length;
                    return parseFloat(avg.toFixed(1));
                }),
                backgroundColor: sourceColorMap[src],
            })),
        });

        // --- Chart 3: Avg Found % by Brand per Source --- top 10 brands, rest → "Others" ---
        setFoundPrcChart({
            labels: completenessLabels,
            datasets: sources.map((src) => ({
                label: src,
                data: completenessLabels.map((label) => {
                    const brandSet = label === 'Others' ? otherBrands : [label];
                    const rows = chartData.filter((r) => r.source === src && brandSet.includes(r.brand));
                    if (!rows.length) return null;
                    const avg = rows.reduce((s, r) => s + r.foundPrc, 0) / rows.length;
                    return parseFloat(avg.toFixed(1));
                }),
                backgroundColor: sourceColorMap[src],
            })),
        });
        // --- Chart 4: Aggregated metrics + per-brand metrics (only when a source is selected) ---
        if (appliedFilters.source) {
            const METRIC_LABELS = [
                'Overall', 'Basic Fields', 'CO₂', 'Weight', 'Sizes',
                'Engine', 'Equipment', 'Prices', 'Supplier Fields',
            ];
            const METRIC_KEYS = [
                'nonEmptyFieldsPct', 'basicFieldsPct', 'co2Pct', 'weightPct',
                'sizesPct', 'engineFieldsPct', 'simplEqPct', 'pricesPct', 'supplierFieldsPct',
            ];
            // Filter brand-scoped data by source explicitly
            const metricRows = brandFilteredData.filter((r) => r.source === appliedFilters.source);
            if (!metricRows.length) {
                setMetricsAggChart(null);
                setMetricsRadarChart(null);
            } else {
                const aggAvg = (key) =>
                    parseFloat((metricRows.reduce((s, r) => s + (r[key] ?? 0), 0) / metricRows.length).toFixed(1));
                setMetricsAggChart({
                    labels: METRIC_LABELS,
                    datasets: [{
                        label: appliedFilters.source,
                        data: METRIC_KEYS.map(aggAvg),
                        backgroundColor: sourceColorMap[appliedFilters.source],
                    }],
                });

                // When source is selected: break down by brand, top 10 by foundCount, rest → Others
                const brandTotalMap = {};
                metricRows.forEach((r) => { brandTotalMap[r.brand] = (brandTotalMap[r.brand] ?? 0) + r.foundCount; });
                const sortedBrands = Object.entries(brandTotalMap).sort((a, b) => b[1] - a[1]);
                const top10Brands  = sortedBrands.slice(0, 10).map(([b]) => b);
                const otherBrands  = sortedBrands.slice(10).map(([b]) => b);
                const radarLabels  = otherBrands.length ? [...top10Brands, 'Others'] : top10Brands;
                setMetricsRadarChart({
                    labels: METRIC_LABELS,
                    datasets: radarLabels.map((label, i) => {
                        const brandSet = label === 'Others' ? otherBrands : [label];
                        const rows = metricRows.filter((r) => brandSet.includes(r.brand));
                        const avg = (key) =>
                            parseFloat((rows.reduce((s, r) => s + (r[key] ?? 0), 0) / rows.length).toFixed(1));
                        return {
                            label,
                            data: METRIC_KEYS.map(avg),
                            backgroundColor: distinctColor(i),
                        };
                    }),
                });
            }
        } else if (appliedFilters.brand) {
            // Brand filtered, no source: show Metric Breakdown by Source
            const METRIC_LABELS = [
                'Overall', 'Basic Fields', 'CO₂', 'Weight', 'Sizes',
                'Engine', 'Equipment', 'Prices', 'Supplier Fields',
            ];
            const METRIC_KEYS = [
                'nonEmptyFieldsPct', 'basicFieldsPct', 'co2Pct', 'weightPct',
                'sizesPct', 'engineFieldsPct', 'simplEqPct', 'pricesPct', 'supplierFieldsPct',
            ];
            setMetricsAggChart(null);
            // Top 10 sources by foundCount, rest → Others
            const srcTotalMap = {};
            brandFilteredData.forEach((r) => { srcTotalMap[r.source] = (srcTotalMap[r.source] ?? 0) + r.foundCount; });
            const sortedSrcs  = Object.entries(srcTotalMap).sort((a, b) => b[1] - a[1]);
            const top10Srcs   = sortedSrcs.slice(0, 10).map(([s]) => s);
            const otherSrcs   = sortedSrcs.slice(10).map(([s]) => s);
            const srcLabels   = otherSrcs.length ? [...top10Srcs, 'Others'] : top10Srcs;
            setMetricsRadarChart({
                labels: METRIC_LABELS,
                datasets: srcLabels.map((label) => {
                    const srcSet = label === 'Others' ? otherSrcs : [label];
                    const rows = brandFilteredData.filter((r) => srcSet.includes(r.source));
                    const avg = (key) =>
                        parseFloat((rows.reduce((s, r) => s + (r[key] ?? 0), 0) / rows.length).toFixed(1));
                    return {
                        label,
                        data: METRIC_KEYS.map(avg),
                        backgroundColor: label === 'Others' ? '#bab0ac' : sourceColorMap[label],
                    };
                }),
            });
        } else {
            setMetricsAggChart(null);
            setMetricsRadarChart(null);
        }
    }, [data, appliedFilters.brand, appliedFilters.source]);

    const backendParamsChanged = (a, b) =>
        a.market !== b.market ||
        a.startedFrom !== b.startedFrom ||
        a.startedTo !== b.startedTo;

    const filtersAreDirty = () => {
        const dateEq = (a, b) =>
            (a == null && b == null) || (a != null && b != null && a.getTime() === b.getTime());
        return (
            filters.market     !== appliedFilters.market     ||
            filters.brand      !== appliedFilters.brand      ||
            filters.source     !== appliedFilters.source     ||
            !dateEq(filters.startedFrom, appliedFilters.startedFrom) ||
            !dateEq(filters.startedTo,   appliedFilters.startedTo)
        );
    };

    const handleApply = () => {
        setAppliedFilters(filters);
        if (backendParamsChanged(filters, appliedFilters)) {
            fetchData(filters);
        }
    };

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const token = await fetchAccessToken(getAccessTokenSilently);
            const body = {
                brand: appliedFilters.brand ?? null,
                market: appliedFilters.market ?? null,
                source: appliedFilters.source ?? null,
                startedFrom: toLocalDateStr(appliedFilters.startedFrom),
                startedTo: toLocalDateStr(appliedFilters.startedTo),
            };
            const response = await fetch(`${API_BASE_URL}/Dashboard/stats-download`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            if (!response.ok) await apiCallErrorHandler(response, 'Failed to download stats');
            const disposition = response.headers.get('Content-Disposition') ?? '';
            const match = disposition.match(/filename=([^;]+)/);
            const filename = match ? match[1].trim().replace(/^"|"$/g, '') : 'opticonfig_stats.xlsx';
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
        } catch (ex) {
            toast.current?.show({ severity: 'error', summary: 'Download failed', detail: ex.message });
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div className="p-4">
            <Toast ref={toast} />
            <h2 className="mb-3">OptiConfig Dashboard</h2>

            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-4 align-items-end">
                <div className="flex flex-column gap-1">
                    <label htmlFor="fromDate">From</label>
                    <Calendar
                        inputId="fromDate"
                        value={filters.startedFrom}
                        onChange={(e) => setFilters((f) => ({ ...f, startedFrom: e.value }))}
                        dateFormat="dd.mm.yy"
                        showIcon
                        showButtonBar
                        disabled={loading}
                    />
                </div>
                <div className="flex flex-column gap-1">
                    <label htmlFor="toDate">To</label>
                    <Calendar
                        inputId="toDate"
                        value={filters.startedTo}
                        onChange={(e) => setFilters((f) => ({ ...f, startedTo: e.value }))}
                        dateFormat="dd.mm.yy"
                        showIcon
                        showButtonBar
                        disabled={loading}
                    />
                </div>
                <div className="flex flex-column gap-1">
                    <label htmlFor="marketFilter">Market</label>
                    <Dropdown
                        inputId="marketFilter"
                        value={filters.market}
                        options={filterOptions.markets}
                        onChange={(e) => setFilters((f) => ({ ...f, market: e.value }))}
                        placeholder="All markets"
                        showClear
                        filter
                        scrollHeight="400px"
                        style={{ minWidth: '10rem' }}
                        disabled={loading}
                    />
                </div>
                <div className="flex flex-column gap-1">
                    <label htmlFor="brandFilter">Brand</label>
                    <Dropdown
                        inputId="brandFilter"
                        value={filters.brand}
                        options={filterOptions.brands}
                        onChange={(e) => setFilters((f) => ({ ...f, brand: e.value }))}
                        placeholder="All brands"
                        showClear
                        filter
                        scrollHeight="400px"
                        style={{ minWidth: '10rem' }}
                        disabled={loading}
                    />
                </div>
                <div className="flex flex-column gap-1">
                    <label htmlFor="sourceFilter">Source</label>
                    <Dropdown
                        inputId="sourceFilter"
                        value={filters.source}
                        options={filterOptions.sources}
                        onChange={(e) => setFilters((f) => ({ ...f, source: e.value }))}
                        placeholder="All sources"
                        showClear
                        filter
                        scrollHeight="400px"
                        style={{ minWidth: '10rem' }}
                        disabled={loading}
                    />
                </div>
                <Button
                    label="Apply"
                    icon="pi pi-search"
                    onClick={handleApply}
                    disabled={loading}
                />
                <Button
                    label="Download"
                    icon="pi pi-download"
                    severity="secondary"
                    onClick={handleDownload}
                    loading={downloading}
                    disabled={data.length === 0 || filtersAreDirty() || loading}
                />
            </div>

            {filtersAreDirty() && (
                <div className="flex align-items-center gap-2 mb-3 px-3 py-2 border-round"
                    style={{ background: '#fff8e1', border: '1px solid #f9a825', color: '#7b5800' }}>
                    <i className="pi pi-exclamation-triangle" style={{ fontSize: '1rem' }} />
                    <span>Filters have changed since the last call. Click Apply to reflect the current selection.</span>
                </div>
            )}

            {!loading && viewStats && (
                <div className="flex flex-wrap gap-4 mb-4 p-3 surface-100 border-round">
                    <span><strong>Sessions:</strong> {viewStats.sessions}</span>
                    <span><strong>Brands:</strong> {viewStats.brands}</span>
                    <span><strong>Sources:</strong> {viewStats.sources}</span>
                    <span><strong>Samples:</strong> {viewStats.vins.toLocaleString()}</span>
                    <span><strong>VIN Count:</strong> {viewStats.vinsCount.toLocaleString()}</span>
                </div>
            )}

            {loading && (
                <div className="flex justify-content-center mt-4">
                    <ProgressSpinner />
                </div>
            )}

            {!loading && data.length === 0 && (
                <p className="text-color-secondary">No data found. Adjust the filters and click Apply.</p>
            )}

            {!loading && data.length > 0 && (
                <div className="grid">
                    {sourceUtilizationChart && (
                        <div className="col-12 lg:col-6 mb-4">
                            <ChartTitle
                                id="tip-utilization"
                                title="Source Utilization (%)"
                                tooltip="% of sessions in which each source returned at least one result. Denominator is the total number of sessions matching the current filters."
                            />
                            <Chart type="bar" data={sourceUtilizationChart} options={completenessOptions} />
                        </div>
                    )}
                    {foundPrcChart && (
                        <div className="col-12 lg:col-6 mb-4">
                            <ChartTitle
                                id="tip-coverage"
                                title="Coverage (%) by Brand & Source"
                                tooltip="Average % of VINs in a session that were found by each source, grouped by brand (top 10 by total found count). Averaged across all matching sessions."
                            />
                            <Chart type="bar" data={foundPrcChart} options={completenessOptions} />
                        </div>
                    )}
                    {sourceCompletenessChart && (
                        <div className={`${appliedFilters.source ? 'col-12 lg:col-6' : 'col-12'} mb-4`}>
                            <ChartTitle
                                id="tip-completeness"
                                title="Data Completeness (%) by Brand & Source"
                                tooltip="Average % of non-empty fields across all 63 tracked fields, per brand per source. Averaged across all matching VINs."
                            />
                            <Chart type="bar" data={sourceCompletenessChart} options={completenessOptions} />
                        </div>
                    )}
                    {metricsAggChart && (
                        <div className={`${appliedFilters.source ? 'col-12 lg:col-6' : 'col-12'} mb-4`}>
                            <ChartTitle
                                id="tip-metrics-agg"
                                title={`Metric Breakdown — ${appliedFilters.source}`}
                                tooltip="Average % fill rate per metric group for the selected source, across all matching VINs. Groups: Overall (63), Basic Fields (Make, Model, Version, Registration Year, Gearbox, Engine Energy, Four Wheel Drive — 7), CO₂ (1), Weight (2), Sizes (4), Engine (4), Equipment (15), Prices (6), Supplier Fields (Registration Date, Registration Year, Kms, Damages, Damage Report, Supplier Offer, Loading Park Info — 7)."
                            />
                            <Chart type="bar" data={metricsAggChart} options={completenessOptions} />
                        </div>
                    )}
                    {metricsRadarChart && (
                        <div className="col-12 mb-4">
                            <ChartTitle
                                id="tip-metrics-breakdown"
                                title={appliedFilters.source
                                    ? `Metric Breakdown by Brand — ${appliedFilters.source}`
                                    : `Metric Breakdown by Source — ${appliedFilters.brand}`}
                                tooltip={appliedFilters.source
                                    ? 'Average % fill rate per metric group, split by brand. Groups: Overall (63), Basic Fields (Make, Model, Version, Registration Year, Gearbox, Engine Energy, Four Wheel Drive — 7), CO₂ (1), Weight (2), Sizes (4), Engine (4), Equipment (15), Prices (6), Supplier Fields (Registration Date, Registration Year, Kms, Damages, Damage Report, Supplier Offer, Loading Park Info — 7). Each bar group is one metric; each bar within it is a brand.'
                                    : 'Average % fill rate per metric group, split by source. Groups: Overall (63), Basic Fields (Make, Model, Version, Registration Year, Gearbox, Engine Energy, Four Wheel Drive — 7), CO₂ (1), Weight (2), Sizes (4), Engine (4), Equipment (15), Prices (6), Supplier Fields (Registration Date, Registration Year, Kms, Damages, Damage Report, Supplier Offer, Loading Park Info — 7). Each bar group is one metric; each bar within it is a source.'}
                            />
                            <Chart type="bar" data={metricsRadarChart} options={completenessOptions} />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
